import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Button,
  TextField,
  Link,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { versionSystem } from "../../../package.json";
import { nomeEmpresa } from "../../../package.json";
import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import logo from "../../assets/logo.png";
import InputMask from "react-input-mask";

const B = "#059669";
const BD = "#047857";
const BL = "#10B981";
const SIDEBAR = "#0F172A";

const UserSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muito curto!").max(50, "Muito longo!").required("Obrigatório"),
  password: Yup.string().min(5, "Muito curto!").max(50, "Muito longo!").required("Obrigatório"),
  email: Yup.string().email("Email inválido").required("Obrigatório"),
  phone: Yup.string().required("Obrigatório"),
});

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100vw",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8fafb",
    padding: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      alignItems: "flex-start",
      paddingTop: theme.spacing(4),
    },
  },
  card: {
    width: "100%",
    maxWidth: 440,
    backgroundColor: "#fff",
    borderRadius: 16,
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    padding: theme.spacing(4, 4, 3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3, 2.5, 2.5),
      borderRadius: 12,
    },
  },
  logo: {
    maxWidth: 200,
    width: "100%",
    height: "auto",
    marginBottom: theme.spacing(2),
  },
  formTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: SIDEBAR,
    marginBottom: theme.spacing(0.5),
    alignSelf: "flex-start",
  },
  formSubtitle: {
    fontSize: 14,
    color: "rgba(0,0,0,0.5)",
    marginBottom: theme.spacing(2.5),
    alignSelf: "flex-start",
  },
  form: {
    width: "100%",
  },
  input: {
    marginBottom: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: "#fff",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: B,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: B,
    },
  },
  submit: {
    margin: theme.spacing(2, 0, 1.5),
    borderRadius: 10,
    fontWeight: 700,
    padding: "12px 0",
    fontSize: 15,
    background: `linear-gradient(135deg, ${B} 0%, ${BD} 100%)`,
    color: "#fff",
    boxShadow: "0 4px 14px rgba(5,150,105,0.3)",
    textTransform: "none",
    "&:hover": {
      background: `linear-gradient(135deg, ${BL} 0%, ${B} 100%)`,
      boxShadow: "0 6px 20px rgba(5,150,105,0.35)",
    },
  },
  linkRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1.5),
    color: "rgba(0,0,0,0.6)",
    fontSize: 13,
    fontWeight: 500,
  },
  linkText: {
    color: B,
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  copyright: {
    marginTop: theme.spacing(2),
    fontSize: 12,
    color: "rgba(0,0,0,0.35)",
    textAlign: "center",
  },
  selectControl: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: B,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: B,
    },
  },
}));

const Signup = () => {
  const classes = useStyles();
  const [plans, setPlans] = useState([]);

  React.useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data } = await openApi.get("/plans/list");
        setPlans(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchPlans();
  }, []);

  const initialValues = { name: "", email: "", phone: "", password: "", planId: "" };

  const handleSignup = async (values, actions) => {
    try {
      await openApi.post("/auth/signup", values);
      toast.success(i18n.t("signup.toasts.success"));
      window.location.href = "/login";
    } catch (err) {
      toastError(err);
    }
    actions.setSubmitting(false);
  };

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <img className={classes.logo} src={logo} alt={nomeEmpresa} />

        <Typography className={classes.formTitle}>Criar conta</Typography>
        <Typography className={classes.formSubtitle}>Preencha seus dados para começar</Typography>

        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={UserSchema}
          onSubmit={handleSignup}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form className={classes.form}>
              <Field
                as={TextField}
                variant="outlined" margin="normal" fullWidth required
                id="name" label={i18n.t("signup.form.name")} name="name"
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
                autoFocus className={classes.input}
              />
              <Field
                as={TextField}
                variant="outlined" margin="normal" fullWidth required
                id="email" label={i18n.t("signup.form.email")} name="email"
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                className={classes.input}
              />
              <Field name="phone">
                {({ field }) => (
                  <InputMask
                    mask="(99) 99999-9999"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  >
                    {(inputProps) => (
                      <TextField
                        {...inputProps}
                        variant="outlined" margin="normal" fullWidth required
                        id="phone" label={i18n.t("signup.form.phone")} name="phone"
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={touched.phone && errors.phone}
                        className={classes.input}
                      />
                    )}
                  </InputMask>
                )}
              </Field>
              <Field
                as={TextField}
                variant="outlined" margin="normal" fullWidth required
                name="password" label={i18n.t("signup.form.password")}
                type="password" id="password"
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                className={classes.input}
              />
              {plans.length > 0 && (
                <FormControl variant="outlined" fullWidth className={classes.selectControl}>
                  <InputLabel id="plan-label">Plano</InputLabel>
                  <Select
                    labelId="plan-label"
                    id="planId"
                    label="Plano"
                    value={values.planId}
                    onChange={(e) => setFieldValue("planId", e.target.value)}
                  >
                    {plans.map((plan) => (
                      <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Button
                type="submit" fullWidth variant="contained"
                className={classes.submit} disabled={isSubmitting}
              >
                {i18n.t("signup.buttons.submit")}
              </Button>
            </Form>
          )}
        </Formik>

        <div className={classes.linkRow}>
          <span>Já tem uma conta?</span>
          <Link component={RouterLink} to="/login" className={classes.linkText}>
            Entrar
          </Link>
        </div>

        <div className={classes.copyright}>
          {nomeEmpresa} &middot; v{versionSystem} &middot; {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
};

export default Signup;
